import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, Clock, Database, Download, HardDriveDownload, HardDriveUpload, Loader2, RefreshCw, Trash2, Upload,
} from "lucide-react";
import { api, dt, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MODES = [
  ["merge", "Gabung (Upsert)", "Data dengan ID sama ditimpa, data lain tetap ada. Paling aman."],
  ["replace", "Ganti Total (Replace)", "Data lama pada cakupan Anda dihapus, lalu diganti isi backup."],
];

const fileSize = (n) => {
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = Number(n || 0);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

export default function Backup() {
  const { user } = useAuth();
  const [tab, setTab] = useState("backup");
  const [stats, setStats] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [includeLogs, setIncludeLogs] = useState(true);
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("merge");
  const [file, setFile] = useState(null);
  const [adopt, setAdopt] = useState(true);
  const [relink, setRelink] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind:'file'|'snapshot', snap}
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const [s, l, c] = await Promise.all([
        api.get("/backup/stats"),
        api.get("/backup/snapshots"),
        api.get("/backup/config"),
      ]);
      setStats(s.data);
      setSnaps(l.data);
      setCfg(c.data);
    } catch (e) { toast.error(errMsg(e)); }
    try {
      const { data } = await api.get("/backup/relink-router");
      setRelink(data);
    } catch (e) { console.warn("relink info:", e?.message || e); }
  };

  const runRelink = async () => {
    setBusy("relink");
    try {
      const { data } = await api.post("/backup/relink-router", null, { timeout: 300000 });
      toast.success(`${Number(data.updated_total).toLocaleString("id-ID")} dokumen diarahkan ke router aktif`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const saveCfg = async (patch) => {
    const next = { enabled: cfg?.enabled ?? false, keep: cfg?.keep ?? 7, include_logs: cfg?.include_logs ?? true, ...patch };
    setCfg((c) => ({ ...c, ...next }));
    try {
      const { data } = await api.put("/backup/config", next);
      setCfg(data);
      toast.success(patch.enabled === false ? "Backup otomatis dinonaktifkan" : "Pengaturan backup otomatis disimpan");
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const runAuto = async () => {
    setBusy("auto");
    try {
      const { data } = await api.post("/backup/run-auto", null, { timeout: 300000 });
      const r = (data.results || [])[0];
      if (r?.ok) toast.success(`Snapshot otomatis dibuat (${r.total} dokumen${r.pruned ? `, ${r.pruned} versi lama dihapus` : ""})`);
      else toast.error(r?.error || "Backup otomatis gagal");
      load();
      setTab("snapshot");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  useEffect(() => { load(); }, []);

  const download = async () => {
    setBusy("download");
    try {
      const res = await api.get("/backup/export", { params: { include_logs: includeLogs }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1]
        || `alphabill-backup-${new Date().toISOString().slice(0, 10)}.json`;
      saveBlob(res.data, name);
      toast.success("Backup berhasil diunduh");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const makeSnapshot = async () => {
    setBusy("snapshot");
    try {
      const fd = new FormData();
      fd.append("note", note);
      fd.append("include_logs", includeLogs ? "true" : "false");
      const { data } = await api.post("/backup/snapshots", fd);
      toast.success(`Snapshot tersimpan (${data.total} dokumen)`);
      setNote("");
      load();
      setTab("snapshot");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const runRestore = async () => {
    const target = confirm;
    setConfirm(null);
    setBusy("restore");
    setResult(null);
    try {
      let data;
      if (target.kind === "file") {
        const fd = new FormData();
        fd.append("file", target.file);
        fd.append("mode", mode);
        fd.append("adopt", adopt ? "true" : "false");
        data = (await api.post("/backup/restore", fd, { timeout: 300000 })).data;
      } else {
        data = (await api.post(`/backup/snapshots/${target.snap._id}/restore`, null, { params: { mode, adopt }, timeout: 300000 })).data;
      }
      setResult(data);
      if (data.restored_total > 0) toast.success(`Restore selesai: ${data.restored_total} dokumen dipulihkan`);
      else toast.error("Tidak ada dokumen yang dipulihkan — cek keterangan di panel hasil restore");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const downloadSnap = async (s) => {
    try {
      const res = await api.get(`/backup/snapshots/${s._id}/download`, { responseType: "blob" });
      saveBlob(res.data, `${s.filename || s._id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeSnap = async (s) => {
    try {
      await api.delete(`/backup/snapshots/${s._id}`);
      toast.success("Snapshot dihapus");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const scopeText = user?.role === "superadmin"
    ? "Cakupan: SELURUH database (semua admin, teknisi, mitra, user, pelanggan, infrastruktur, setting)."
    : "Cakupan: hanya data milik akun admin Anda (akun turunan, pelanggan, infrastruktur, voucher, setting).";

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div className="rounded-2xl border border-border bg-card/60 glass p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600">
              <Database className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display text-lg font-semibold tracking-tight">Backup &amp; Restore Database</p>
              <p className="text-xs text-muted-foreground">{scopeText}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl border border-border px-4 py-2 text-right">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total dokumen</p>
              <p className="font-mono text-lg font-semibold" data-testid="backup-total">{(stats?.total ?? 0).toLocaleString("id-ID")}</p>
            </div>
            <Button variant="outline" size="icon" className="rounded-full" onClick={load} data-testid="backup-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {relink?.available && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="relink-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-100">
                {Number(relink.stale).toLocaleString("id-ID")} dokumen masih menunjuk router lama
              </p>
              <p className="text-[11px] leading-relaxed text-amber-200/80">
                Biasanya terjadi setelah memulihkan backup dari instalasi lama. Arahkan data ini ke router aktif
                Anda supaya pelanggan, voucher, dan infrastruktur kembali tampil.
              </p>
            </div>
          </div>
          <Button variant="outline" className="rounded-xl border-amber-500/40" onClick={runRelink} disabled={busy === "relink"} data-testid="btn-relink">
            {busy === "relink" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Arahkan ke Router Aktif
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-xl">
          <TabsTrigger value="backup" data-testid="tab-backup">Backup</TabsTrigger>
          <TabsTrigger value="restore" data-testid="tab-restore">Restore</TabsTrigger>
          <TabsTrigger value="snapshot" data-testid="tab-snapshot">Snapshot Server ({snaps.length})</TabsTrigger>
        </TabsList>

        {/* ---------------- BACKUP ---------------- */}
        <TabsContent value="backup" className="mt-5 space-y-5">
          <div className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
            <div className="space-y-4 rounded-2xl border border-border bg-card/60 glass p-5">
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Sertakan data log &amp; riwayat</p>
                  <p className="text-[11px] text-muted-foreground">Audit, riwayat hotspot, notifikasi, riwayat isolir</p>
                </div>
                <Switch checked={includeLogs} onCheckedChange={setIncludeLogs} data-testid="include-logs" />
              </div>
              <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-[11px] leading-relaxed text-amber-600 dark:text-amber-300">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                File backup memuat hash password akun dan kredensial router (terenkripsi). Simpan di tempat aman
                dan jangan dibagikan.
              </p>
              <Button className="w-full rounded-xl" onClick={download} disabled={busy === "download"} data-testid="btn-download-backup">
                {busy === "download" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HardDriveDownload className="mr-2 h-4 w-4" />}
                Unduh File Backup (.json)
              </Button>
              <div className="space-y-2 rounded-xl border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="flex items-center gap-1.5 text-sm font-medium">
                      <Clock className="h-3.5 w-3.5 text-cyan-400" /> Backup otomatis harian
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Jalan sendiri {cfg?.schedule || "02:00 WIB setiap hari"} tanpa klik manual
                    </p>
                  </div>
                  <Switch checked={!!cfg?.enabled} onCheckedChange={(v) => saveCfg({ enabled: v })} data-testid="auto-enabled" />
                </div>
                <div className="flex items-center justify-between gap-2 rounded-lg bg-secondary/40 px-2.5 py-2">
                  <span className="text-[11px] text-muted-foreground">Versi disimpan</span>
                  <select
                    value={cfg?.keep ?? 7} data-testid="auto-keep"
                    onChange={(e) => saveCfg({ keep: Number(e.target.value) })}
                    className="rounded-lg border border-border bg-card px-2 py-1 text-xs"
                  >
                    {[3, 5, 7, 14, 30].map((n) => (<option key={n} value={n}>{n} terakhir</option>))}
                  </select>
                </div>
                <div className="flex items-center justify-between gap-2 rounded-lg bg-secondary/40 px-2.5 py-2">
                  <span className="text-[11px] text-muted-foreground">Sertakan log &amp; riwayat</span>
                  <Switch checked={cfg?.include_logs ?? true} onCheckedChange={(v) => saveCfg({ include_logs: v })} data-testid="auto-include-logs" />
                </div>
                {cfg?.last_run && (
                  <p className="text-[11px] text-muted-foreground" data-testid="auto-last-run">
                    Terakhir: {dt(cfg.last_run)} ·{" "}
                    <span className={cfg.last_status === "ok" ? "text-emerald-500" : "text-destructive"}>
                      {cfg.last_status === "ok" ? `berhasil (${Number(cfg.last_total || 0).toLocaleString("id-ID")} dokumen)` : cfg.last_status}
                    </span>
                  </p>
                )}
                <Button variant="outline" className="w-full rounded-xl" onClick={runAuto} disabled={busy === "auto"} data-testid="btn-run-auto">
                  {busy === "auto" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Clock className="mr-2 h-4 w-4" />}
                  Jalankan Backup Otomatis Sekarang
                </Button>
              </div>

              <div className="space-y-2 rounded-xl border border-border p-3">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Simpan snapshot di server</Label>
                <Input
                  className="h-11 rounded-xl bg-secondary/50" placeholder="Catatan (opsional), mis. sebelum migrasi router"
                  value={note} onChange={(e) => setNote(e.target.value)} data-testid="snapshot-note"
                />
                <Button variant="outline" className="w-full rounded-xl" onClick={makeSnapshot} disabled={busy === "snapshot"} data-testid="btn-snapshot">
                  {busy === "snapshot" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
                  Simpan Snapshot di Server
                </Button>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card/60 glass p-5">
              <p className="mb-3 text-sm font-medium">Isi backup</p>
              <div className="max-h-[420px] overflow-auto rounded-xl border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Jenis</TableHead>
                      <TableHead className="text-right">Dokumen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(stats?.collections || []).map((c) => (
                      <TableRow key={c.collection} data-testid={`stat-${c.collection}`}>
                        <TableCell className="text-xs">
                          {c.label}
                          <span className="ml-1 font-mono text-[10px] text-muted-foreground">{c.collection}</span>
                        </TableCell>
                        <TableCell>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] ${c.group === "log" ? "bg-secondary text-muted-foreground" : "bg-cyan-500/15 text-cyan-500"}`}>
                            {c.group === "log" ? "log" : "utama"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{Number(c.count).toLocaleString("id-ID")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ---------------- RESTORE ---------------- */}
        <TabsContent value="restore" className="mt-5 space-y-5">
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-4 rounded-2xl border border-border bg-card/60 glass p-5">
              <p className="text-sm font-medium">1. Pilih mode restore</p>
              <div className="space-y-2">
                {MODES.map(([val, label, desc]) => (
                  <button
                    key={val} type="button" onClick={() => setMode(val)} data-testid={`mode-${val}`}
                    className={`w-full rounded-xl border p-3 text-left transition-colors ${mode === val ? "border-violet-500/50 bg-violet-500/10" : "border-border hover:bg-secondary"}`}
                  >
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-[11px] text-muted-foreground">{desc}</p>
                  </button>
                ))}
              </div>

              <p className="text-sm font-medium">2. Pilih file backup</p>
              <input
                ref={inputRef} type="file" accept=".json,.gz,application/json" data-testid="restore-file"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                className="block w-full cursor-pointer rounded-xl border border-border bg-secondary/50 p-2.5 text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-violet-600 file:px-3 file:py-1.5 file:text-white"
              />
              {file && (
                <p className="rounded-xl border border-border p-2.5 text-[11px] text-muted-foreground">
                  {file.name} · {fileSize(file.size)}
                </p>
              )}
              {user?.role !== "superadmin" && (
                <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Alihkan kepemilikan data ke akun ini</p>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      Nyalakan bila backup dibuat oleh akun/instalasi lama (mis. akun dibuat ulang). Tanpa ini
                      semua dokumen akan dilewati karena pemiliknya berbeda.
                    </p>
                  </div>
                  <Switch checked={adopt} onCheckedChange={setAdopt} data-testid="adopt-switch" />
                </div>
              )}
              <Button
                className="w-full rounded-xl" disabled={!file || busy === "restore"} data-testid="btn-restore"
                onClick={() => setConfirm({ kind: "file", file })}
              >
                {busy === "restore" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HardDriveUpload className="mr-2 h-4 w-4" />}
                Mulai Restore
              </Button>
              <p className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-[11px] leading-relaxed text-destructive">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                Mode Ganti Total menghapus data saat ini sebelum memasukkan isi backup. Disarankan mengunduh
                backup terbaru terlebih dahulu.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card/60 glass p-5">
              <p className="mb-3 text-sm font-medium">Hasil restore</p>
              {!result ? (
                <p className="text-xs text-muted-foreground">Belum ada proses restore pada sesi ini.</p>
              ) : (
                <div className="space-y-3" data-testid="restore-result">
                  <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
                    {[["Dipulihkan", result.restored_total], ["Dihapus", result.deleted_total], ["Dilewati", result.skipped_total], ["Gagal", result.failed_total || 0]].map(([l, v]) => (
                      <div key={l} className="rounded-xl border border-border p-3">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{l}</p>
                        <p className="font-mono text-base font-semibold">{Number(v).toLocaleString("id-ID")}</p>
                      </div>
                    ))}
                  </div>
                  {result.note && (
                    <p className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3 text-[11px] leading-relaxed text-cyan-200" data-testid="restore-note">
                      {result.note}
                    </p>
                  )}
                  {result.restored_total === 0 && (
                    <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-[11px] leading-relaxed text-amber-200" data-testid="restore-empty-hint">
                      Tidak ada dokumen yang dipulihkan. Kalau backup ini milik akun/instalasi lama, nyalakan
                      saklar &quot;Alihkan kepemilikan data ke akun ini&quot; lalu ulangi restore.
                    </p>
                  )}
                  {(result.errors || []).length > 0 && (
                    <ul className="space-y-1 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-[10px] text-destructive">
                      {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                    </ul>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    Sumber: {result.source?.created_by || "-"} · {dt(result.source?.created_at)} · mode {result.mode}
                  </p>
                  <div className="max-h-[320px] overflow-auto rounded-xl border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead className="text-right">Pulih</TableHead>
                          <TableHead className="text-right">Hapus</TableHead>
                          <TableHead className="text-right">Lewat</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(result.collections || {}).map(([k, v]) => (
                          <TableRow key={k}>
                            <TableCell className="text-xs">{v.label || k}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-emerald-500">{v.restored}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-muted-foreground">{v.deleted}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-muted-foreground">{v.skipped}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ---------------- SNAPSHOT ---------------- */}
        <TabsContent value="snapshot" className="mt-5">
          <div className="rounded-2xl border border-border bg-card/60 glass p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium">Riwayat snapshot di server</p>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                Mode restore aktif:
                <span className="rounded-full bg-secondary px-2 py-0.5 font-medium text-foreground">
                  {mode === "replace" ? "Ganti Total" : "Gabung"}
                </span>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setMode(mode === "replace" ? "merge" : "replace")} data-testid="toggle-mode">
                  ubah
                </Button>
              </div>
            </div>
            <div className="overflow-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Waktu</TableHead>
                    <TableHead>Oleh</TableHead>
                    <TableHead>Catatan</TableHead>
                    <TableHead className="text-right">Dokumen</TableHead>
                    <TableHead className="text-right">Ukuran</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {snaps.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-xs text-muted-foreground">Belum ada snapshot.</TableCell></TableRow>
                  )}
                  {snaps.map((s) => (
                    <TableRow key={s._id} data-testid={`snap-${s._id}`}>
                      <TableCell className="text-xs">{dt(s.created_at)}</TableCell>
                      <TableCell className="text-xs">{s.created_by} <span className="text-muted-foreground">({s.role})</span></TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {s.auto && (
                          <span className="mr-1 rounded-full bg-cyan-500/15 px-2 py-0.5 text-[10px] text-cyan-500">otomatis</span>
                        )}
                        {s.note || (s.auto ? "" : "-")}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">{Number(s.total).toLocaleString("id-ID")}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fileSize(s.size)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" className="rounded-full" title="Unduh" onClick={() => downloadSnap(s)} data-testid={`snap-download-${s._id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="rounded-full" title="Restore" onClick={() => setConfirm({ kind: "snapshot", snap: s })} data-testid={`snap-restore-${s._id}`}>
                            <Upload className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="rounded-full text-destructive" title="Hapus" onClick={() => removeSnap(s)} data-testid={`snap-delete-${s._id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-restore">
          <AlertDialogHeader>
            <AlertDialogTitle>Jalankan restore {mode === "replace" ? "Ganti Total" : "Gabung"}?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                {mode === "replace"
                  ? "Semua data pada cakupan Anda akan DIHAPUS lalu diganti dengan isi backup. Tindakan ini tidak dapat dibatalkan."
                  : "Data dengan ID yang sama akan ditimpa isi backup, data lain tetap ada."}
              </span>
              <span className="block text-xs">
                Sumber: {confirm?.kind === "file" ? confirm?.file?.name : confirm?.snap?.filename}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={runRestore} data-testid="confirm-restore-ok">
              Ya, restore sekarang
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
